import React, { ComponentType } from 'react';
import PaddingBox from '@uds/padding-box';
import { Container } from '@uds/grid';
import { Heading04, BodyCopy } from '@uds/typography';
import { useBreakpoints } from '@uds/responsive';
import { CTA } from 'core/ribbons/cta';
import { RibbonProps, Type } from 'domain/constants/ribbons';
import Shortcode, { ShortcodeProps } from 'components/Shortcode';
import Ribbon from 'components/ribbons/Ribbon';
import CallToAction from 'components/common/CallToAction';
import visibilitySet from 'presentation/hocs/visibilitySet';
import errorBoundary from 'presentation/hocs/errorBoundary';
import { isNilOrEmpty } from 'presentation/utils/generalUtils';
import findResponsiveItem from 'presentation/utils/findResponsiveItem';
import { Alignment } from 'domain/constants/alignment';

import getClassNames from './MessageStrip.stylesheet';

interface OuterProps extends RibbonProps {
  text: {
    title: ShortcodeProps;
    subtitle: ShortcodeProps;
  };
  ctas: Array<CTA>;
}

export const PureMessageStrip = ({ id, loading, layout, text, ctas, colorway, name, hBaseline = 1 }: OuterProps) => {
  const title = text?.title ?? null;
  const subtitle = text?.subtitle ?? null;
  const hasTitle = !isNilOrEmpty(title);
  const hasSubtitle = !isNilOrEmpty(subtitle);
  const hasCta = !isNilOrEmpty(ctas);

  const alignment = findResponsiveItem(useBreakpoints(), [
    layout?.alignmentMobile ?? Alignment.STARTING,
    layout?.alignmentTablet ?? Alignment.STARTING,
    layout?.alignment ?? Alignment.STARTING,
  ]);

  const classNames = getClassNames({
    alignment,
  });
  return (
    <Ribbon
      id={id}
      colorway={colorway}
      layout={layout}
      loading={loading}
      flush={true}
      name={name}
      type={Type.MESSAGE_STRIP}
    >
      <div className={classNames.root}>
        <Container>
          <PaddingBox>
            <div className={classNames.container}>
              <div className={classNames.content}>
                {hasTitle && (
                  <Heading04 depth={hBaseline}>
                    <Shortcode against={colorway} code={title} />
                  </Heading04>
                )}
                {hasSubtitle && (
                  <BodyCopy className={classNames.subtitle}>
                    <Shortcode against={colorway} code={subtitle} />
                  </BodyCopy>
                )}
              </div>
              {hasCta && (
                <div className={classNames.ctas}>
                  {ctas.map((cta, i) => (
                    <div key={`message_strip_cta_${id}_`.concat(String(i))} className={classNames.cta}>
                      <CallToAction ribbonId={id} index={i} {...cta} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </PaddingBox>
        </Container>
      </div>
    </Ribbon>
  );
};

PureMessageStrip.displayName = 'MessageStrip';

export default errorBoundary(visibilitySet(PureMessageStrip)) as ComponentType<OuterProps>;
